import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, XCircle, Clock, Eye, Search, Building, X, User, FileText, Calendar, Heart, Car, Upload, Download, Shield } from 'lucide-react';
import apiClient from '../api/client';
import { ClaimDto, CompanyDto } from '../types/dtos';
import LoadingSpinner from '../components/LoadingSpinner';
import FeedbackDialog, { FeedbackType } from '../components/FeedbackDialog';
import { resolveUrl } from '../utils/urlUtils';

export default function Claims() {
  const [claims, setClaims] = useState<ClaimDto[]>([]);
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClaim, setSelectedClaim] = useState<ClaimDto | null>(null);
  const [rejectionNotes, setRejectionNotes] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  const [approvedAmount, setApprovedAmount] = useState<string>('');
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [showApprovePrompt, setShowApprovePrompt] = useState(false);

  const [feedback, setFeedback] = useState<{
    isOpen: boolean;
    type: FeedbackType;
    title: string;
    message: string;
    primaryActionLabel?: string;
    onPrimaryAction?: () => void;
    secondaryActionLabel?: string;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });

  const triggerFeedback = (config: Omit<typeof feedback, 'isOpen'>) => {
    setFeedback({ ...config, isOpen: true });
  };

  const isSuperAdmin = user?.company === 'Xtrem Group';

  // 1. Fetch Profile & Base Data
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const profileRes = await apiClient.get('/admin/profile');
        setUser(profileRes.data);

        if (profileRes.data.company === 'Xtrem Group') {
          const companiesRes = await apiClient.get<CompanyDto[]>('/admin/companies');
          setCompanies(companiesRes.data);
        }
      } catch (err: any) {
        console.error("Failed to load initial data", err);
        setError(err.response?.data?.message || "Failed to load management tools.");
      }
    };
    fetchInitialData();
  }, []);

  // 2. Fetch Claims (depends on selectedCompany)
  useEffect(() => {
    const fetchClaims = async () => {
      setLoading(true);
      try {
        const url = selectedCompany
          ? `/admin/claims?companyId=${selectedCompany}`
          : '/admin/claims';
        const res = await apiClient.get<ClaimDto[]>(url);
        setClaims(res.data);
      } catch (err: any) {
        console.error(err);
        setError(err.response?.data?.message || "Failed to load claims.");
      } finally {
        setLoading(false);
      }
    };
    fetchClaims();
  }, [selectedCompany]);

  // 3. Handle Actions
  const handleStatusUpdate = async (id: string, status: string, notes?: string, amount?: number) => {
    if (status === 'Approved') {
      try {
        await apiClient.put(`/admin/claims/${id}/status`, {
          status,
          adminNotes: notes,
          approvedAmount: amount
        });
        setClaims(prev => prev.map(c => c.id === id ? { ...c, status, settledAmount: amount ?? c.claimedAmount, approvalNotes: notes } : c));
        setSelectedClaim(null);
        setShowApprovePrompt(false);
        setApprovalNotes('');
        setApprovedAmount('');
        triggerFeedback({
          type: 'success',
          title: 'Claim Approved',
          message: `The claim has been successfully approved.`,
        });
      } catch (error: any) {
        console.error('Failed to update claim status:', error);
        triggerFeedback({
          type: 'error',
          title: 'Update Failed',
          message: error.response?.data?.message || 'Failed to approve the claim. Please try again.',
        });
      }
    } else {
      if (status === 'Rejected' && !notes) return;

      try {
        await apiClient.put(`/admin/claims/${id}/status`, { status, adminNotes: notes });
        setClaims(prev => prev.map(c => c.id === id ? { ...c, status } : c));
        setSelectedClaim(null);
        setShowRejectPrompt(false);
        setRejectionNotes('');
        triggerFeedback({
          type: 'success',
          title: 'Claim Rejected',
          message: `Claim has been rejected.`,
        });
      } catch (error: any) {
        console.error('Failed to update claim status:', error);
        triggerFeedback({
          type: 'error',
          title: 'Update Failed',
          message: error.response?.data?.message || 'Failed to update status.',
        });
      }
    }
  };

  // CSV Export
  const handleExportCsv = () => {
    const headers = ['Claim ID', 'Customer', 'Product', 'Type', 'Amount (MK)', 'Submitted On', 'Status', 'Claimant Name', 'Claimant Phone', 'Claimant Email', 'Deceased Name', 'Deceased National ID', 'Vehicle Reg', 'Accident Location', 'Police Report Ref', 'Description'];
    const rows = filteredClaims.map(c => [
      c.claimId,
      c.customerName || '',
      c.productName || '',
      c.type,
      c.claimedAmount,
      new Date(c.submittedAt ?? c.date).toLocaleDateString(),
      c.status,
      c.claimantName || '',
      c.claimantPhone || '',
      c.claimantEmail || '',
      c.deceasedName || '',
      c.deceasedNationalId || '',
      c.insuredVehicleRegistration || '',
      c.accidentLocation || '',
      c.policeReportReference || '',
      (c.description || '').replace(/"/g, '""'),
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(v => `"${v}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `claims-export-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // 4. Filtering
  const filteredClaims = claims.filter(c => {
    const matchesTab = activeTab === 'All' || c.status === activeTab;
    const matchesSearch =
      c.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.claimId.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Approved': return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs flex items-center w-fit gap-1"><CheckCircle size={12} /> Approved</span>;
      case 'Rejected': return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs flex items-center w-fit gap-1"><XCircle size={12} /> Rejected</span>;
      default: return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs flex items-center w-fit gap-1"><Clock size={12} /> {status}</span>;
    }
  };

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto">

          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center mb-6 gap-6">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Claims Management</h1>
              <p className="mt-1 text-sm text-gray-500">Review and manage insurance claims</p>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
              {isSuperAdmin && (
                <div className="flex-1 sm:flex-initial flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-2 shadow-sm">
                  <Building size={16} className="text-gray-400" />
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="text-sm font-medium focus:outline-none bg-transparent w-full"
                  >
                    <option value="">All Companies</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex-1 sm:flex-initial relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  placeholder="Search ID or Name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full sm:w-64 text-sm focus:ring-2 focus:ring-blue-900 outline-none shadow-sm"
                />
              </div>
              <button
                onClick={handleExportCsv}
                disabled={filteredClaims.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium shadow-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                title="Export visible claims to CSV"
              >
                <Download size={16} className="text-gray-400" />
                Export CSV
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6">
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 col-span-1">
              <div className="text-gray-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider">Pending Review</div>
              <div className="text-xl sm:text-3xl font-bold text-yellow-600 mt-2">
                {claims.filter(c => c.status === 'Pending').length}
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 col-span-1">
              <div className="text-gray-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider">Approved (Month)</div>
              <div className="text-xl sm:text-3xl font-bold text-green-600 mt-2">
                {claims.filter(c => c.status === 'Approved').length}
              </div>
            </div>
            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-200 col-span-2 lg:col-span-1">
              <div className="text-gray-400 text-[10px] sm:text-xs font-bold uppercase tracking-wider">Total Settled</div>
              <div className="text-xl sm:text-3xl font-bold text-blue-900 mt-2 truncate">
                MK {claims.reduce((sum, c) => sum + (c.settledAmount || 0), 0).toLocaleString()}
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-2 mb-4 overflow-x-auto no-scrollbar pb-2 md:pb-0">
            {['All', 'Pending', 'Approved', 'Rejected'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-bold transition-all border ${activeTab === tab ? 'bg-blue-900 text-white border-blue-900 shadow-md' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'
                  }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-12 flex justify-center"><LoadingSpinner /></div>
            ) : error ? (
              <div className="p-12 text-center text-red-600 font-medium">
                {error}
              </div>
            ) : (
              <div className="bg-white">
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-gray-50 text-gray-500 font-bold border-b uppercase text-[11px]">
                      <tr>
                        <th className="px-6 py-4">Claim ID</th>
                        <th className="px-6 py-4">Customer</th>
                        <th className="px-6 py-4 text-[11px]">Product & Type</th>
                        <th className="px-6 py-4">Submitted On</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {filteredClaims.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                            No claims found matching your criteria.
                          </td>
                        </tr>
                      ) : filteredClaims.map((claim) => (
                        <tr key={claim.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 font-mono text-xs text-gray-500">{claim.claimId}</td>
                          <td className="px-6 py-4 font-bold text-gray-900">
                            {claim.customerId ? (
                              <Link 
                                to={`/admin/customers/${claim.customerId}`}
                                className="text-blue-600 hover:text-blue-900 hover:underline transition-colors"
                              >
                                {claim.customerName}
                              </Link>
                            ) : (
                              claim.customerName
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-bold text-gray-900">
                              {claim.isManualPolicy ? (claim.manualPolicyName || 'Manual Policy') : (claim.productName || 'General')}
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-xs text-gray-500 font-medium">{claim.type}</div>
                              {claim.isManualPolicy && (
                                <span className="text-[9px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100 uppercase tracking-tighter">
                                  Manual
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-500 font-medium">{new Date(claim.submittedAt ?? claim.date).toLocaleDateString()}</td>
                          <td className="px-6 py-4">{getStatusBadge(claim.status)}</td>
                          <td className="px-6 py-4 text-right flex justify-end gap-2">
                            {claim.status === 'Pending' && (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedClaim(claim);
                                    setShowApprovePrompt(true);
                                  }}
                                  className="px-3 py-1 bg-green-50 text-green-700 rounded border border-green-200 hover:bg-green-100 text-[10px] font-bold uppercase transition"
                                >
                                  Approve
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedClaim(claim);
                                    setShowRejectPrompt(true);
                                  }}
                                  className="px-3 py-1 bg-red-50 text-red-700 rounded border border-red-200 hover:bg-red-100 text-[10px] font-bold uppercase transition"
                                >
                                  Reject
                                </button>
                              </>
                            )}
                            <button
                              onClick={() => {
                                setSelectedClaim(claim);
                                setShowRejectPrompt(false);
                              }}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md transition"
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden divide-y divide-gray-100">
                  {filteredClaims.length === 0 ? (
                    <div className="p-12 text-center text-gray-500 text-sm font-medium">No claims match your filter.</div>
                  ) : filteredClaims.map((claim) => (
                    <div key={claim.id} className="p-4 bg-white hover:bg-gray-50 transition-colors">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-[9px] text-gray-400 font-bold uppercase">ID: {claim.claimId}</p>
                          <div className="flex items-center gap-2 mb-1">
                            {claim.customerId ? (
                              <Link 
                                to={`/admin/customers/${claim.customerId}`}
                                className="font-bold text-gray-900 hover:text-blue-900 hover:underline transition-colors"
                              >
                                {claim.customerName}
                              </Link>
                            ) : (
                              <h4 className="font-bold text-gray-900">{claim.customerName}</h4>
                            )}
                            {claim.isManualPolicy && (
                              <span className="text-[8px] font-black text-amber-600 bg-amber-50 px-1 py-0.5 rounded border border-amber-100 uppercase tracking-tighter">
                                Manual
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          {getStatusBadge(claim.status)}
                          <button
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowRejectPrompt(false);
                            }}
                            className="p-2 text-blue-600 bg-blue-50 rounded-lg"
                          >
                            <Eye size={18} />
                          </button>
                        </div>
                      </div>

                      <div className="mb-3">
                        <p className="text-[9px] text-gray-400 font-bold uppercase">Product</p>
                        <p className="text-sm border-l-2 border-blue-900 pl-2 font-medium text-gray-700">
                          {claim.isManualPolicy ? (claim.manualPolicyName || 'Manual Policy') : (claim.productName || 'General')}
                        </p>
                      </div>

                      <div className="p-3 bg-gray-50 rounded-lg">
                        <p className="text-[9px] text-gray-400 font-bold uppercase">Submitted On</p>
                        <p className="text-[11px] text-gray-600 font-medium">{new Date(claim.submittedAt ?? claim.date).toLocaleDateString()}</p>
                      </div>

                      {claim.status === 'Pending' && (
                        <div className="grid grid-cols-2 gap-2 mt-4">
                          <button
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowApprovePrompt(true);
                            }}
                            className="flex items-center justify-center gap-1 py-2 bg-green-600 text-white rounded font-bold text-xs shadow-sm"
                          >
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button
                            onClick={() => {
                              setSelectedClaim(claim);
                              setShowRejectPrompt(true);
                            }}
                            className="flex items-center justify-center gap-1 py-2 bg-red-600 text-white rounded font-bold text-xs shadow-sm"
                          >
                            <XCircle size={14} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
      </div>
    </div>

      {/* Claim Details Modal */}
      {selectedClaim && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-5 flex justify-between items-center shrink-0 rounded-t-2xl">
              <div>
                <h2 className="text-2xl font-bold text-white">Claim Details</h2>
                <p className="text-blue-100 text-sm font-mono mt-1">{selectedClaim.claimId}</p>
              </div>
              <button
                onClick={() => {
                  setSelectedClaim(null);
                  setShowRejectPrompt(false);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1">
              {/* Status Badge */}
              <div className="mb-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {selectedClaim.status === 'Approved' ? (
                    <div className="bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                      <CheckCircle size={18} />
                      Approved
                    </div>
                  ) : selectedClaim.status === 'Rejected' ? (
                    <div className="bg-red-100 text-red-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                      <XCircle size={18} />
                      Rejected
                    </div>
                  ) : (
                    <div className="bg-yellow-100 text-yellow-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2">
                      <Clock size={18} />
                      {selectedClaim.status}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase tracking-wide">
                    {selectedClaim.status === 'Approved' ? 'Settled Amount' : 'Claimed Amount'}
                  </div>
                  <div className={`text-2xl font-bold ${selectedClaim.status === 'Approved' ? 'text-green-600' : 'text-blue-900'}`}>
                    MK {(selectedClaim.status === 'Approved' ? selectedClaim.settledAmount : selectedClaim.claimedAmount).toLocaleString()}
                  </div>
                  {selectedClaim.status === 'Approved' && selectedClaim.settledAmount !== selectedClaim.claimedAmount && (
                    <div className="text-[10px] text-gray-400 mt-1 italic">
                      Claimed: MK {selectedClaim.claimedAmount.toLocaleString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Customer Information */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <User className="text-blue-900" size={20} />
                    <h3 className="font-bold text-gray-900">Customer Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Customer Name</label>
                      <p className="font-medium text-gray-900">{selectedClaim.customerName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Product / Policy</label>
                      <div className="flex items-center gap-2">
                         <p className="font-medium text-gray-900">
                           {selectedClaim.isManualPolicy ? (selectedClaim.manualPolicyName || 'Manual Policy') : (selectedClaim.productName || 'N/A')}
                         </p>
                         {selectedClaim.isManualPolicy && (
                            <span className="text-[9px] font-black text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-100 uppercase tracking-tighter">
                              Manual
                            </span>
                         )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Claim Information */}
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="text-blue-900" size={20} />
                    <h3 className="font-bold text-gray-900">Claim Information</h3>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Claim Type</label>
                      <p className="font-medium text-gray-900">{selectedClaim.type}</p>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Submitted On</label>
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-gray-400" />
                        <p className="font-medium text-gray-900">
                          {new Date(selectedClaim.submittedAt ?? selectedClaim.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 uppercase tracking-wide">Incident Date</label>
                      <div className="flex items-center gap-2">
                        <Calendar size={14} className="text-orange-400" />
                        <p className="font-medium text-gray-900">
                          {new Date(selectedClaim.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Manual Policy Specifics ✅ NEW */}
                {selectedClaim.isManualPolicy && (
                  <div className="bg-amber-50/50 rounded-xl p-5 border border-amber-100 md:col-span-2">
                    <div className="flex items-center gap-2 mb-4 text-amber-900">
                      <Shield size={20} />
                      <h3 className="font-bold uppercase text-[10px] tracking-widest text-[#92400e]">Unlinked / Manual Policy Details</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-[10px] text-amber-800 uppercase font-black tracking-widest">Policy Name</label>
                        <p className="font-bold text-gray-900">{selectedClaim.manualPolicyName || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-[10px] text-amber-800 uppercase font-black tracking-widest">Policy Number</label>
                        <p className="font-bold text-gray-900 font-mono text-xs">{selectedClaim.manualPolicyNumber || 'N/A'}</p>
                      </div>
                      <div>
                        <label className="text-[10px] text-amber-800 uppercase font-black tracking-widest">Category</label>
                        <p className="font-bold text-gray-900">{selectedClaim.manualPolicyType || 'General'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Claimant Information (If different or for third-party/funeral) */}
                <div className="bg-blue-50/30 rounded-xl p-5 border border-blue-100 md:col-span-2">
                  <div className="flex items-center gap-2 mb-4">
                    <User className="text-blue-900" size={20} />
                    <h3 className="font-bold text-gray-900">Claimant & Contact Details</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest">Name</label>
                      <p className="font-bold text-gray-900">{selectedClaim.claimantName || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest">Phone</label>
                      <p className="font-bold text-gray-900">{selectedClaim.claimantPhone || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest">Email</label>
                      <p className="font-bold text-gray-900">{selectedClaim.claimantEmail || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Funeral Specifics */}
                {(selectedClaim.deceasedName || selectedClaim.deceasedNationalId) && (
                  <div className="bg-rose-50/30 rounded-xl p-5 border border-rose-100 md:col-span-2">
                    <div className="flex items-center gap-2 mb-4 text-rose-900">
                      <Heart size={20} />
                      <h3 className="font-bold">Funeral Claim Specifics</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-[10px] text-rose-600 uppercase font-black tracking-widest">Deceased Name</label>
                        <p className="font-bold text-gray-900">{selectedClaim.deceasedName}</p>
                      </div>
                      <div>
                        <label className="text-[10px] text-rose-600 uppercase font-black tracking-widest">Deceased National ID</label>
                        <p className="font-bold text-gray-900">{selectedClaim.deceasedNationalId || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Third-Party Motor Specifics */}
                {(selectedClaim.insuredVehicleRegistration || selectedClaim.accidentLocation) && (
                  <div className="bg-emerald-50/30 rounded-xl p-5 border border-emerald-100 md:col-span-2">
                    <div className="flex items-center gap-2 mb-4 text-emerald-900">
                      <Car size={20} />
                      <h3 className="font-bold">Third-Party Motor Details</h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="text-[10px] text-emerald-600 uppercase font-black tracking-widest">Insured Vehicle</label>
                        <p className="font-bold text-gray-900 italic">{selectedClaim.insuredVehicleRegistration}</p>
                      </div>
                      <div>
                        <label className="text-[10px] text-emerald-600 uppercase font-black tracking-widest">Accident Location</label>
                        <p className="font-bold text-gray-900">{selectedClaim.accidentLocation}</p>
                      </div>
                      <div>
                        <label className="text-[10px] text-emerald-600 uppercase font-black tracking-widest">Police Report Ref</label>
                        <p className="font-bold text-gray-900">{selectedClaim.policeReportReference || 'N/A'}</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Dynamic Claim Details ✅ NEW */}
                {selectedClaim.detailsJson && (() => {
                    try {
                        const details = JSON.parse(selectedClaim.detailsJson);
                        const entries = Object.entries(details).filter(([k]) => k !== 'documents');
                        if (entries.length === 0) return null;
                        
                        return (
                          <div className="bg-blue-50/20 rounded-xl p-5 border border-blue-100 md:col-span-2">
                            <div className="flex items-center gap-2 mb-4 text-blue-900">
                              <FileText size={20} />
                              <h3 className="font-bold uppercase text-[10px] tracking-widest text-blue-900">Additional Claim Information</h3>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              {entries.map(([key, value]) => (
                                <div key={key}>
                                  <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest">{key.replace(/([A-Z])/g, ' $1').trim()}</label>
                                  <p className="font-bold text-gray-900 text-sm">{String(value)}</p>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                    } catch (e) { return null; }
                })()}

                {/* Description - Full Width */}
                <div className="md:col-span-2 bg-gray-50 rounded-xl p-5 border border-gray-200">
                  <div className="flex items-center gap-2 mb-4">
                    <FileText className="text-blue-900" size={20} />
                    <h3 className="font-bold text-gray-900">Incident Narrative</h3>
                  </div>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-wrap italic">
                    "{selectedClaim.description || 'No description provided.'}"
                  </p>
                </div>

                {/* Approval Notes - Full Width (If Approved) */}
                {selectedClaim.status === 'Approved' && selectedClaim.approvalNotes && (
                  <div className="md:col-span-2 bg-green-50 rounded-xl p-5 border border-green-100">
                    <div className="flex items-center gap-2 mb-4">
                      <CheckCircle className="text-green-600" size={20} />
                      <h3 className="font-bold text-gray-900">Settlement Notes</h3>
                    </div>
                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                      {selectedClaim.approvalNotes}
                    </p>
                  </div>
                )}

                {/* Supporting Documents */}
                {(() => {
                  let submittedKyc = false;
                  if (selectedClaim.detailsJson) {
                    try {
                      submittedKyc = JSON.parse(selectedClaim.detailsJson)._kycSubmitted === true;
                    } catch { }
                  }
                  
                  const showKycDoc = Boolean(selectedClaim.customerNationalIdUrl && submittedKyc);
                  const docCount = (selectedClaim.documents?.length || 0) + (showKycDoc ? 1 : 0);

                  if (docCount === 0) return null;

                  return (
                    <div className="md:col-span-2 bg-white rounded-xl p-5 border border-gray-200">
                      <div className="flex items-center gap-2 mb-4">
                        <Upload className="text-blue-900" size={20} />
                        <h3 className="font-bold text-gray-900">
                          Supporting Evidence ({docCount})
                        </h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {/* Customer National ID ✅ NEW */}
                        {showKycDoc && (
                          <a
                            href={resolveUrl(selectedClaim.customerNationalIdUrl!)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-3 p-3 bg-blue-50/50 hover:bg-blue-50 border border-blue-200 rounded-xl transition-all group"
                          >
                            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-blue-600 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors">
                              <Shield size={20} />
                            </div>
                            <div className="overflow-hidden">
                              <p className="text-xs font-bold text-gray-900 truncate mb-0.5">National ID / Passport</p>
                              <p className="text-[10px] text-blue-600 font-bold uppercase tracking-widest">KYC Document</p>
                            </div>
                          </a>
                        )}

                      {/* Other Claim Documents */}
                      {selectedClaim.documents?.map((doc) => (
                        <a
                          key={doc.id}
                          href={resolveUrl(doc.filePath)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 rounded-xl transition-all group"
                        >
                          <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-blue-600 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors">
                            <FileText size={20} />
                          </div>
                          <div className="overflow-hidden">
                            <p className="text-xs font-bold text-gray-900 truncate mb-0.5">{doc.fileName}</p>
                            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">{doc.documentType || 'Document'}</p>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                );
              })()}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 shrink-0 rounded-b-2xl">
              {showRejectPrompt ? (
                <div className="space-y-3">
                  <label className="text-xs font-bold text-gray-500 uppercase">Reason for Rejection <span className="text-red-500">*</span></label>
                  <textarea
                    value={rejectionNotes}
                    onChange={(e) => setRejectionNotes(e.target.value)}
                    placeholder="Explain why the claim is being rejected (e.g. Policy not active, lack of evidence)"
                    className="w-full p-3 border rounded-lg text-sm focus:ring-2 focus:ring-red-500 outline-none"
                    rows={3}
                    autoFocus
                  />
                  <div className="flex gap-2 justify-end">
                    <button
                      onClick={() => { setShowRejectPrompt(false); setRejectionNotes(''); }}
                      className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-lg transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedClaim.id, 'Rejected', rejectionNotes)}
                      disabled={!rejectionNotes.trim()}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <XCircle size={16} /> Confirm Rejection
                    </button>
                  </div>
                </div>
              ) : showApprovePrompt ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-bold text-gray-500 uppercase">Approved Amount (MK) <span className="text-red-500">*</span></label>
                      <input
                        type="number"
                        value={approvedAmount || selectedClaim.claimedAmount}
                        onChange={(e) => setApprovedAmount(e.target.value)}
                        className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none mt-1 font-bold text-blue-900"
                        placeholder="Enter amount..."
                      />
                      <p className="text-[10px] text-gray-400 mt-1 italic">Claimed: MK {selectedClaim.claimedAmount.toLocaleString()}</p>
                    </div>
                    <div className="flex flex-col justify-end">
                      <p className="text-[11px] text-gray-500 mb-2">Adjust the approved amount if necessary. Default is the full claimed amount.</p>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase">Approval Notes <span className="text-gray-400">(optional)</span></label>
                    <textarea
                      value={approvalNotes}
                      onChange={(e) => setApprovalNotes(e.target.value)}
                      placeholder="Add any internal notes or instructions for the customer regarding this settlement..."
                      className="w-full p-3 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none mt-1"
                      rows={3}
                    />
                  </div>

                  <div className="flex gap-2 justify-end pt-2">
                    <button
                      onClick={() => { setShowApprovePrompt(false); setApprovalNotes(''); setApprovedAmount(''); }}
                      className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-100 rounded-lg transition"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleStatusUpdate(selectedClaim.id, 'Approved', approvalNotes, parseFloat(approvedAmount) || selectedClaim.claimedAmount)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 flex items-center gap-2 shadow-sm transition-all"
                    >
                      <CheckCircle size={16} /> Confirm Approval
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    Claim ID: <span className="font-mono font-semibold text-gray-900">{selectedClaim.claimId}</span>
                  </div>
                  <div className="flex gap-3">
                    {selectedClaim.status === 'Pending' && (
                      <>
                        <button
                          onClick={() => setShowApprovePrompt(true)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors flex items-center gap-2"
                        >
                          <CheckCircle size={18} />
                          Approve Claim
                        </button>
                        <button
                          onClick={() => setShowRejectPrompt(true)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium transition-colors flex items-center gap-2"
                        >
                          <XCircle size={18} />
                          Reject Claim
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => {
                        setSelectedClaim(null);
                        setShowRejectPrompt(false);
                        setShowApprovePrompt(false);
                      }}
                      className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <FeedbackDialog
        isOpen={feedback.isOpen}
        onClose={() => setFeedback(prev => ({ ...prev, isOpen: false }))}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        primaryActionLabel={feedback.primaryActionLabel}
        onPrimaryAction={feedback.onPrimaryAction}
        secondaryActionLabel={feedback.secondaryActionLabel}
      />
    </div>
  );
}
